import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Button, FormControl, InputLabel, MenuItem, Select, TextField, Typography,
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    CircularProgress, IconButton, InputAdornment, Alert, Grid, Autocomplete,
    Chip, Popover, useMediaQuery, Tooltip, Tabs, Tab, ListSubheader, AlertTitle
} from '@mui/material';

import { format, parseISO, isAfter, isBefore, addDays, parse } from 'date-fns';
import { useSelector } from 'react-redux';
import { useTheme } from '@mui/material/styles';
import { apiRoute } from '../../constants/enum';
import { usePost, useFetchWithBody } from '../../api/reactQueryCustomHooks';
import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import { autoTable } from "jspdf-autotable"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faClose, faDownload, faFilter, faInbox, faInfo, faRefresh, faSearch } from '@fortawesome/free-solid-svg-icons';
import { ErrorCard, LoadingCard, MainContainer } from '../../components';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import dayjs from "dayjs"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"

const OutstandingReport = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { compCode, compName } = useSelector((state) => state.company);
    const tableRef = useRef(null);

    const [formData, setFormData] = useState({
        out_type: 'R',
        party_id: '',
        party_name: '',
        diff_duedate: new Date(),
        showAge1: 0,
        showAge2: 0,
        compcode: compCode,
    });

    const [showReport, setShowReport] = useState(false);
    const [detailsData, setDetailsData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterField, setFilterField] = useState('inv_date');
    const [filteredData, setFilteredData] = useState([]);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(new Date());
    const [anchorEl, setAnchorEl] = useState(null);
    const [currentFilterColumn, setCurrentFilterColumn] = useState('');
    const [groupBy, setGroupBy] = useState('none');
    const [activeTab, setActiveTab] = useState('all');

    // Column filters state
    const [columnFilters, setColumnFilters] = useState({
        Party: '',
        invDate: '',
        voutype: '',
        invno: '',
        DueDays: '',
        DueDate: '',
        invAmt: '',
        TdsAmt: '',
        PayAmt: '',
        retAmt: '',
        BalanceAmt: '',
        dif_day_inv_date: '',
        dif_day_due_date: '',
        Broker: ''
    });

    const {
        isLoading: isLoadingAccounts,
        data: accountsData = [],
        isError: isAccountsError,
        error: accountsError,
    } = useFetchWithBody({
        url: apiRoute.outstanding.getAccount,
        queryKey: ['accounts'],
        inputData: {},
    });

    const filteredAccounts = accountsData.filter(account =>
        account.AC_DESC.toLowerCase().includes(searchTerm.toLowerCase()) ||
        account.AC_ID.toString().includes(searchTerm)
    );

    const {
        mutate: fetchReport,
        isPending: isFetchingDetails,
        isError: isDetailsError,
        error: detailsError
    } = usePost({
        url: apiRoute.outstanding.getReportData,
        queryKey: 'partyDetails',
        onSuccessTasks: (data) => {
            const formattedData = data.map(item => ({
                Party: item.party || '',
                invDate: item.inv_date ? format(parseISO(item.inv_date), 'dd-MM-yyyy') : '',
                voutype: item.vou_type || '',
                invno: item.inv_no || '',
                DueDays: item.duedays || 0,
                DueDate: item.duedate ? format(parseISO(item.duedate), 'dd-MM-yyyy') : '',
                invAmt: parseFloat(item.inv_amount) || 0,
                TdsAmt: parseFloat(item.tds_amount) || 0,
                PayAmt: parseFloat(item.pay_amount) || 0,
                retAmt: parseFloat(item.ret_amount) || 0,
                BalanceAmt: parseFloat(item.balance_amount) || 0,
                dif_day_inv_date: item.diff_days_inv_date || 0,
                dif_day_due_date: item.diff_days_due_date || 0,
                Broker: item.broker || ''
            }));
            setDetailsData(formattedData);
            setFilteredData(formattedData);
            setShowReport(true);
            // setFromDate(null);
            // setToDate(null);
            setFilterField('inv_date');
        },
    });

    const filteredAndGroupedData = useMemo(() => {
        if (!detailsData.length) return [];

        // First apply column filters
        let data = detailsData.filter(row => {
            return Object.entries(columnFilters).every(([key, value]) => {
                if (!value) return true;
                const rowValue = row[key] || '';
                return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
            });
        });

        // Then apply date range filter
        if (fromDate || toDate) {
            data = data.filter(item => {
                const dateStr = item[filterField === 'invDate' ? 'invDate' : 'DueDate'];
                if (!dateStr) return false;

                // Parse the date string (format: dd-MM-yyyy)
                const [day, month, year] = dateStr.split('-');
                const date = new Date(`${year}-${month}-${day}`);

                // Compare dates
                if (fromDate && isBefore(date, fromDate)) return false;
                if (toDate && !isBefore(date, addDays(toDate, 1))) return false; // inclusive toDate
                return true;
            });
        }

        // Apply grouping if selected
        if (groupBy !== 'none') {
            const groupedData = {};

            data.forEach(row => {
                let groupKey;

                switch (groupBy) {
                    case 'broker':
                        groupKey = row.Broker || 'Broker';
                        break;
                    case 'party':
                        groupKey = row.Party || 'Unknown Party';
                        break;
                    default:
                        groupKey = 'all';
                }

                if (!groupedData[groupKey]) {
                    groupedData[groupKey] = {
                        groupKey,
                        Party: groupBy === 'party' ? row.Party : 'Multiple Parties',
                        invDate: groupBy === 'none' ? row.invDate : 'Multiple Dates',
                        voutype: groupBy === 'none' ? row.voutype : 'Multiple',
                        invno: groupBy === 'none' ? row.invno : 'Multiple',
                        DueDays: 0,
                        DueDate: groupBy === 'none' ? row.DueDate : 'Multiple',
                        invAmt: 0,
                        TdsAmt: 0,
                        PayAmt: 0,
                        retAmt: 0,
                        BalanceAmt: 0,
                        dif_day_inv_date: 0,
                        dif_day_due_date: 0,
                        Broker: groupBy === 'broker' ? row.Broker : 'Multiple Brokers',
                        _children: []
                    };
                }

                // Sum up all numeric values
                groupedData[groupKey].DueDays += parseFloat(row.DueDays) || 0;
                groupedData[groupKey].invAmt += parseFloat(row.invAmt) || 0;
                groupedData[groupKey].TdsAmt += parseFloat(row.TdsAmt) || 0;
                groupedData[groupKey].PayAmt += parseFloat(row.PayAmt) || 0;
                groupedData[groupKey].retAmt += parseFloat(row.retAmt) || 0;
                groupedData[groupKey].BalanceAmt += parseFloat(row.BalanceAmt) || 0;
                groupedData[groupKey].dif_day_inv_date += parseFloat(row.dif_day_inv_date) || 0;
                groupedData[groupKey].dif_day_due_date += parseFloat(row.dif_day_due_date) || 0;

                // Store original rows for drill-down
                groupedData[groupKey]._children.push(row);
            });

            // Convert to array and sort
            data = Object.values(groupedData).sort((a, b) => {
                return a.groupKey.localeCompare(b.groupKey);
            });
        }

        return data;
    }, [detailsData, columnFilters, fromDate, toDate, filterField, groupBy]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handlePartyChange = (event, value) => {
        setFormData(prev => ({
            ...prev,
            party_id: value ? value.AC_ID : '',
            party_name: value ? value.AC_DESC : ''
        }));
    };

    const handleDateChange = (date) => {
        setFormData((prev) => ({ ...prev, diff_duedate: date }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setShowReport(false);
        const formattedData = {
            ...formData,
            diff_duedate: format(formData.diff_duedate, 'yyyy-MM-dd'),
            compcode: compCode,
        };
        fetchReport({ data: formattedData });
    };

    const handleReset = () => {
        setFormData({
            out_type: 'R',
            party_id: '',
            party_name: '',
            diff_duedate: new Date(),
            showAge1: 0,
            showAge2: 0,
            compcode: compCode,
        });
        setSearchTerm('');
        setShowReport(false);
        setDetailsData([]);
        setFilteredData([])
        setFromDate(null);
        setToDate(new Date());
        setFilterField('inv_date');
        const formattedData = {
            out_type: 'R',
            party_id: '',
            party_name: '',
            diff_duedate: new Date(),
            showAge1: 0,
            showAge2: 0,
            compcode: compCode,
            diff_duedate: format(formData.diff_duedate, 'yyyy-MM-dd'),
            compcode: compCode,
        };
        fetchReport({ data: formattedData })
        setGroupBy('none');
    };

    const handleFilterClick = (columnName, event) => {
        setCurrentFilterColumn(columnName);
        setAnchorEl(event.currentTarget);
    };

    const handleFilterClose = () => {
        setAnchorEl(null);
    };

    const clearColumnFilter = (columnName) => {
        setColumnFilters(prev => ({ ...prev, [columnName]: '' }));
    };

    const calculateTotals = () => {
        const totals = {
            invAmt: 0,
            TdsAmt: 0,
            PayAmt: 0,
            retAmt: 0,
            BalanceAmt: 0
        };

        filteredAndGroupedData.forEach(row => {
            totals.invAmt += row.invAmt;
            totals.TdsAmt += row.TdsAmt;
            totals.PayAmt += row.PayAmt;
            totals.retAmt += row.retAmt;
            totals.BalanceAmt += row.BalanceAmt;
        });

        return totals;
    };

    const totals = calculateTotals();

    const formatCurrency = (value) => {
        return value.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    const handleColumnFilterChange = (e) => {
        const { value } = e.target;
        setColumnFilters(prev => ({ ...prev, [currentFilterColumn]: value }));
    };

    const exportToExcel = () => {
        if (filteredAndGroupedData.length === 0) return;

        // Define all possible columns
        const allColumns = [
            { key: 'Party', label: 'Party', width: 30 },
            { key: 'Broker', label: 'Broker', width: 25 },
            { key: 'invDate', label: 'Inv. Date', width: 15 },
            { key: 'voutype', label: 'Bill Type', width: 15 },
            { key: 'invno', label: 'Inv. No', width: 15 },
            { key: 'DueDays', label: 'Due Days', width: 15 },
            { key: 'DueDate', label: 'Due Date', width: 15 },
            { key: 'invAmt', label: 'Inv. Amount', width: 20 },
            { key: 'TdsAmt', label: 'TDS Amount', width: 20 },
            { key: 'PayAmt', label: 'Pay. Amount', width: 20 },
            { key: 'retAmt', label: 'Ret. Amount', width: 20 },
            { key: 'BalanceAmt', label: 'Balance Amount', width: 20 },
            { key: 'dif_day_inv_date', label: 'Diff.Days Inv.Date', width: 15 },
            { key: 'dif_day_due_date', label: 'Diff.Days Due.Date', width: 15 },
        ];

        // Select columns based on groupBy value
        let columnsToShow;
        switch (groupBy) {
            case 'party':
                columnsToShow = allColumns.filter(col =>
                    ['Party', 'invAmt', 'TdsAmt', 'PayAmt', 'retAmt', 'BalanceAmt'].includes(col.key)
                );
                break;
            case 'broker':
                columnsToShow = allColumns.filter(col =>
                    ['Broker', 'invAmt', 'TdsAmt', 'PayAmt', 'retAmt', 'BalanceAmt'].includes(col.key)
                );
                break;
            default:
                columnsToShow = allColumns;
        }

        const headers = columnsToShow.map(col => col.label);

        const rows = filteredAndGroupedData.map(row => {
            return columnsToShow.map(col => {
                const value = row[col.key];

                // Format date columns
                if (col.key === 'invDate' || col.key === 'DueDate') {
                    if (!value) return '';

                    // Check if date is already in dd-MM-yyyy format
                    if (value.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        const [day, month, year] = value.split('-');
                        return `${day}/${month}/${year}`;
                    }

                    // Try to parse other date formats if needed
                    try {
                        const date = new Date(value);
                        if (!isNaN(date)) {
                            return format(date, 'dd/MM/yyyy');
                        }
                    } catch (e) {
                        return value;
                    }
                }

                if (typeof value === 'number') {
                    return value.toFixed(2);
                }
                return value ?? '';
            });
        });

        // Add totals row
        const totalRow = columnsToShow.map(col => {
            switch (col.key) {
                case 'invAmt': return totals.invAmt?.toFixed(2) || '';
                case 'TdsAmt': return totals.TdsAmt?.toFixed(2) || '';
                case 'PayAmt': return totals.PayAmt?.toFixed(2) || '';
                case 'retAmt': return totals.retAmt?.toFixed(2) || '';
                case 'BalanceAmt': return totals.BalanceAmt?.toFixed(2) || '';
                default: return '';
            }
        });
        totalRow[0] = 'TOTAL';
        rows.push(totalRow);

        const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);

        // Set column widths
        ws['!cols'] = columnsToShow.map(col => ({ wch: col.width }));

        // Set number format for currency columns
        const currencyColumns = ['invAmt', 'TdsAmt', 'PayAmt', 'retAmt', 'BalanceAmt'];
        columnsToShow.forEach((col, colIndex) => {
            if (currencyColumns.includes(col.key)) {
                // Apply number format to all rows (including header and total)
                for (let rowIndex = 0; rowIndex <= rows.length; rowIndex++) {
                    const cellAddress = XLSX.utils.encode_cell({ r: rowIndex, c: colIndex });
                    if (ws[cellAddress]) {
                        ws[cellAddress].z = '#,##0.00';
                    }
                }
            }
        });

        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Outstanding Report');

        const fileName = `${formData.out_type === 'R' ? 'Receivable' : 'Payable'}_Outstanding_Report_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
        const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([wbout], { type: 'application/octet-stream' }), fileName);
    };

    const exportToPDF = () => {
        if (filteredAndGroupedData.length === 0) return;

        const doc = new jsPDF({
            orientation: 'landscape',
            unit: 'mm'
        });

        // Set default font
        doc.setFont('helvetica');
        doc.setFontSize(10);

        // Page dimensions
        const pageWidth = doc.internal.pageSize.getWidth();
        const margin = 10;
        const contentWidth = pageWidth - 2 * margin;

        // Company Name - Top Center (Large and bold)
        doc.setFontSize(16);
        doc.setFont('helvetica', 'bold');
        const companyName = compName.length > 0 ? compName.join(', ') : 'Company';
        const companyNameWidth = doc.getStringUnitWidth(companyName) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(companyName, margin + (contentWidth - companyNameWidth) / 2, 20);

        // Report Title - Below Company Name
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        const reportTitle = `${formData.out_type === 'R' ? 'Receivable' : 'Payable'} Outstanding Report`;
        const reportTitleWidth = doc.getStringUnitWidth(reportTitle) * doc.internal.getFontSize() / doc.internal.scaleFactor;
        doc.text(reportTitle, margin + (contentWidth - reportTitleWidth) / 2, 27);

        // Date Range - Left aligned
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');

        // Add a separator line
        doc.setDrawColor(100, 100, 100);
        doc.line(margin, 44, pageWidth - margin, 44);

        // Define all possible columns
        const allColumns = [
            { key: 'Party', label: 'Party', width: 30 },
            { key: 'Broker', label: 'Broker', width: 25 },
            { key: 'invDate', label: 'Inv. Date', width: 20 },
            { key: 'voutype', label: 'Bill Type', width: 15 },
            { key: 'invno', label: 'Inv. No', width: 15 },
            { key: 'DueDays', label: 'Due Days', width: 15 },
            { key: 'DueDate', label: 'Due Date', width: 20 },
            { key: 'invAmt', label: 'Inv. Amount', width: 20 },
            { key: 'TdsAmt', label: 'TDS Amount', width: 20 },
            { key: 'PayAmt', label: 'Pay. Amount', width: 20 },
            { key: 'retAmt', label: 'Ret. Amount', width: 20 },
            { key: 'BalanceAmt', label: 'Balance Amount', width: 20 },
            { key: 'dif_day_inv_date', label: 'Diff.Days Inv.Date', width: 20 },
            { key: 'dif_day_due_date', label: 'Diff.Days Due.Date', width: 20 },
        ];

        // Select columns based on groupBy value
        let columnsToShow;
        switch (groupBy) {
            case 'party':
                columnsToShow = allColumns.filter(col =>
                    ['Party', 'invAmt', 'TdsAmt', 'PayAmt', 'retAmt', 'BalanceAmt'].includes(col.key)
                );
                break;
            case 'broker':
                columnsToShow = allColumns.filter(col =>
                    ['Broker', 'invAmt', 'TdsAmt', 'PayAmt', 'retAmt', 'BalanceAmt'].includes(col.key)
                );
                break;
            default:
                columnsToShow = allColumns;
        }

        const headers = columnsToShow.map(col => col.label);
        const columnStyles = {};
        columnsToShow.forEach((col, index) => {
            columnStyles[index] = {
                cellWidth: col.width,
                overflow: 'linebreak',
                minCellHeight: 5
            };
        });

        const data = filteredAndGroupedData.map(row => {
            return columnsToShow.map(col => {
                const value = row[col.key];

                // Format date columns
                if (col.key === 'invDate' || col.key === 'DueDate') {
                    if (!value) return '';

                    // Check if date is already in dd-MM-yyyy format
                    if (value.match(/^\d{2}-\d{2}-\d{4}$/)) {
                        const [day, month, year] = value.split('-');
                        return `${day}/${month}/${year}`;
                    }

                    // Try to parse other date formats if needed
                    try {
                        const date = new Date(value);
                        if (!isNaN(date)) {
                            return format(date, 'dd/MM/yyyy');
                        }
                    } catch (e) {
                        return value;
                    }
                }

                if (typeof value === 'number') {
                    return value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                }
                return value ?? '';
            });
        });

        // Add totals row
        const totalRow = columnsToShow.map(col => {
            switch (col.key) {
                case 'invAmt': return totals.invAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
                case 'TdsAmt': return totals.TdsAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
                case 'PayAmt': return totals.PayAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
                case 'retAmt': return totals.retAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
                case 'BalanceAmt': return totals.BalanceAmt?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '';
                default: return '';
            }
        });
        totalRow[0] = 'TOTAL';
        data.push(totalRow);

        // AutoTable configuration
        autoTable(doc, {
            head: [headers],
            body: data,
            startY: 47,
            margin: { left: margin, right: margin },
            styles: {
                fontSize: 8,
                cellPadding: 2,
                overflow: 'linebreak',
                halign: 'right',
                valign: 'middle',
                lineWidth: 0.1,
                lineColor: [200, 200, 200],
                font: 'helvetica',
                textColor: [0, 0, 0]
            },
            columnStyles,
            headStyles: {
                fillColor: [41, 128, 185],
                textColor: 255,
                fontSize: 8,
                halign: 'center',
                valign: 'middle',
                fontStyle: 'bold'
            },
            bodyStyles: {
                halign: 'right',
                valign: 'middle',
                fontSize: 8
            },
            alternateRowStyles: {
                fillColor: [245, 245, 245]
            },
            didParseCell: function (dataCell) {
                // First column left aligned
                if (dataCell.column.index === 0) {
                    dataCell.cell.styles.halign = 'left';
                }

                // Bold and centered for totals row
                if (dataCell.row.index === filteredAndGroupedData.length) {
                    dataCell.cell.styles.fontStyle = 'bold';
                    dataCell.cell.styles.fillColor = [220, 220, 220];
                    dataCell.cell.styles.textColor = [0, 0, 0];
                    if (dataCell.column.index === 0) {
                        dataCell.cell.styles.halign = 'center';
                    }
                }
            },
            willDrawPage: function (data) {
                // Add page numbers
                const pageCount = doc.internal.getNumberOfPages();
                doc.setFontSize(8);
                for (let i = 1; i <= pageCount; i++) {
                    doc.setPage(i);
                    doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, doc.internal.pageSize.getHeight() - 10, { align: 'right' });
                }
            }
        });

        const filename = `${formData.out_type === 'R' ? 'Receivable' : 'Payable'}_Outstanding_Report_${format(new Date(), 'ddMMyyyy')}.pdf`;
        doc.save(filename);
    };

    const getReportTitle = () => {
        return formData.out_type === 'R' ? 'Receivable Outstanding Report' : 'Payable Outstanding Report';
    };
    const headCssStyle = { fontSize: "0.7rem", lineHeight: 1.3, textTransform: "uppercase", backgroundColor: '#f0f0f0', fontWeight: 600, color: '#3c3c3c' }
    const headfilterStyle = "text-xs bg-slate-600 radius-md  p-0.5 ml-1 text-white"
    if (isDetailsError) {
        return <ErrorCard />
    }
    if (isFetchingDetails) {
        return <LoadingCard />
    }
    return (
        <MainContainer
            pageName={`Oustanding Report`}
            breadcrumbs={["Reports", "Outstanding Report"]}
        >
            <Box sx={{
                p: isMobile ? 1 : 3,
                backgroundColor: 'background.default',
                minHeight: '100vh'
            }}>
                {/* Header Section */}
                <Box sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    justifyContent: 'space-between',
                    alignItems: { xs: 'flex-start', md: 'center' },
                    gap: 2,
                    mb: 1,
                    p: 1,
                    backgroundColor: 'background.paper',
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)'
                }}>
                    <Typography variant="h4" sx={{
                        fontSize: isMobile ? '1rem' : '1.3rem',
                        fontWeight: 500,
                        color: 'primary.main',
                        letterSpacing: -0.5
                    }}>
                        {getReportTitle()}
                    </Typography>

                    {showReport && (
                        <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1,
                            alignSelf: { xs: 'stretch', md: 'center' }
                        }}>
                            <Button
                                variant="contained"
                                color="success"
                                startIcon={<FontAwesomeIcon icon={faDownload} />}
                                onClick={exportToExcel}
                                disabled={filteredAndGroupedData.length === 0}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: "0.8rem",
                                    px: 2,
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    '&:hover': { boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)' }
                                }}
                            >
                                Export Excel
                            </Button>

                            <Button
                                variant="contained"
                                color="error"
                                startIcon={<FontAwesomeIcon icon={faDownload} />}
                                onClick={exportToPDF}
                                disabled={filteredAndGroupedData.length === 0}
                                sx={{
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: "0.8rem",
                                    px: 2,
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    '&:hover': { boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.2)' }
                                }}
                            >
                                Export PDF
                            </Button>
                        </Box>
                    )}
                </Box>

                {/* Filters Section */}
                <Paper sx={{
                    p: 2,
                    mb: 1,
                    borderRadius: 2,
                    boxShadow: 1,
                    backgroundColor: 'background.paper'
                }}>
                    <form onSubmit={handleSubmit}>
                        <Grid container spacing={2} alignItems="center">
                            {/* Primary Filters */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Type</InputLabel>
                                    <Select
                                        name="out_type"
                                        value={formData.out_type}
                                        onChange={handleChange}
                                        label="Type"
                                    >
                                        <MenuItem value="R">Receivable</MenuItem>
                                        <MenuItem value="G">Payable</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <Autocomplete
                                    options={filteredAccounts}
                                    getOptionLabel={(option) => option.AC_DESC}
                                    value={accountsData.find(account => account.AC_ID === formData.party_id) || null}
                                    onChange={handlePartyChange}
                                    onInputChange={(e, v) => setSearchTerm(v)}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Search Party"
                                            size="small"
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <>
                                                        {isLoadingAccounts && <CircularProgress size={20} />}
                                                        {params.InputProps.endAdornment}
                                                    </>
                                                ),
                                            }}
                                        />
                                    )}
                                    loading={isLoadingAccounts}
                                    noOptionsText="No parties found"
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label={"As On Date"}
                                            views={["day", "month", "year"]}
                                            format="DD/MM/YYYY"
                                            value={
                                                formData.diff_duedate
                                                    ? dayjs(formData.diff_duedate)
                                                    : formData.diff_duedate
                                            }
                                            onChange={(newValue) => {
                                                setFormData((prev) => {
                                                    return {
                                                        ...prev,
                                                        diff_duedate: newValue != '' && newValue != 'Invalid Date' ?
                                                            dayjs(newValue).format("YYYY-MM-DD") : null,
                                                    };
                                                });
                                            }}
                                            //isRequired={item.required}
                                            // className="w-full "
                                            slotProps={{
                                                textField: { size: "small", },
                                            }}
                                        />
                                    </LocalizationProvider>

                                </FormControl>
                            </Grid>

                            {/* Secondary Filters - Disabled initially */}
                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small" disabled={!showReport}>
                                    <InputLabel>Filter By</InputLabel>
                                    <Select
                                        value={filterField}
                                        label="Filter By"
                                        onChange={(e) => setFilterField(e.target.value)}
                                    >
                                        <MenuItem value="invDate">Invoice Date</MenuItem>
                                        <MenuItem value="DueDate">Due Date</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small" disabled={!showReport}>
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label={"From Date"}
                                            views={["day", "month", "year"]}
                                            format="DD/MM/YYYY"
                                            value={
                                                fromDate
                                                    ? dayjs(fromDate)
                                                    : fromDate
                                            }
                                            onChange={(newValue) => {
                                                setFromDate(
                                                    newValue != '' && newValue != 'Invalid Date' ?
                                                        dayjs(newValue).format("YYYY-MM-DD") : null,
                                                )
                                            }}
                                            //isRequired={item.required}
                                            // className="w-full "
                                            slotProps={{
                                                textField: { size: "small", },
                                            }}
                                        />
                                    </LocalizationProvider>

                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small">
                                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                                        <DatePicker
                                            label={"To Date"}
                                            views={["day", "month", "year"]}
                                            format="DD/MM/YYYY"
                                            value={
                                                toDate
                                                    ? dayjs(toDate)
                                                    : toDate
                                            }
                                            onChange={(newValue) => {
                                                setToDate(
                                                    newValue != '' && newValue != 'Invalid Date' ?
                                                        dayjs(newValue).format("YYYY-MM-DD") : null,
                                                )
                                            }}
                                            //isRequired={item.required}
                                            // className="w-full "
                                            slotProps={{
                                                textField: { size: "small", },
                                            }}
                                        />
                                    </LocalizationProvider>

                                </FormControl>
                            </Grid>

                            <Grid item xs={12} sm={6} md={4} lg={3}>
                                <FormControl fullWidth size="small" disabled={!showReport}>
                                    <InputLabel>Order By</InputLabel>
                                    <Select
                                        label="Group By"
                                        value={groupBy}
                                        onChange={(e) => setGroupBy(e.target.value)}
                                    >
                                        <ListSubheader>Detail :</ListSubheader>
                                        <MenuItem value="none">Report Detail</MenuItem>
                                        <ListSubheader>Summary :</ListSubheader>
                                        <MenuItem value="party">Party</MenuItem>
                                        <MenuItem value="broker">Broker</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* Action Buttons - Always at the end */}
                            <Grid item xs={12} sm={6} md={3} lg={2}>
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1.5,
                                    flexDirection: { xs: 'row', sm: 'row-reverse' },
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                                }}>


                                    <Button
                                        variant="outlined"
                                        onClick={handleReset}
                                        startIcon={<FontAwesomeIcon style={{ fontSize: "0.9rem" }} icon={faRefresh} />}
                                        sx={{
                                            minWidth: 100,
                                            // py: 1,
                                            borderRadius: 2,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            borderWidth: 1.5,
                                            '&:hover': { borderWidth: 1.5 },
                                            flexGrow: { xs: 1, sm: 0 }
                                        }}
                                    >
                                        Reset
                                    </Button>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        type="submit"
                                        disabled={isFetchingDetails}
                                        startIcon={isFetchingDetails ? <CircularProgress size={20} color="inherit" /> :
                                            <FontAwesomeIcon style={{ fontSize: "0.9rem" }} icon={faSearch} />
                                        }
                                        sx={{
                                            minWidth: 100,
                                            // py: 0,
                                            borderRadius: 2,
                                            fontWeight: 500,
                                            textTransform: 'none',
                                            flexGrow: { xs: 1, sm: 0 }
                                        }}
                                    >
                                        {isFetchingDetails ? 'Searching' : 'Search'}
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </form>
                </Paper>

                {
                    (isAccountsError || isDetailsError) && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                            <AlertTitle>Error</AlertTitle>
                            {accountsError?.message || detailsError?.message || 'An error occurred'}
                        </Alert>
                    )
                }

                {/* Data Table Section */}
                <Paper sx={{
                    p: isMobile ? 1 : 1,
                    borderRadius: 3,
                    boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08)',
                    backgroundColor: 'background.paper'
                }} ref={tableRef}>
                    {/* Table Header */}
                    {showReport && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            justifyContent: 'space-between',
                            alignItems: { xs: 'flex-start', sm: 'center' },
                            mb: 0,
                            gap: 1,
                            p: 1
                        }}>
                            <Typography variant="h6" sx={{
                                fontWeight: 600,
                                fontSize: "1rem",
                                color: 'text.primary'
                            }}>
                                {filteredAndGroupedData.length > 0 ?
                                    `Showing ${filteredAndGroupedData.length} of ${detailsData.length} records` :
                                    'No data available'}
                            </Typography>

                            {filteredAndGroupedData.length > 0 && (
                                <Box sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    justifyContent: { xs: 'flex-start', sm: 'flex-end' }
                                }}>
                                    {fromDate && (
                                        <Chip
                                            label={`From: ${format(fromDate, 'dd-MMM-yyyy')}`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}
                                    {toDate && (
                                        <Chip
                                            label={`To: ${format(toDate, 'dd-MMM-yyyy')}`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                            sx={{ fontWeight: 500 }}
                                        />
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Data Table */}
                    {showReport ? (

                        <TableContainer
                            component={Paper}
                            sx={{
                                maxHeight: 'calc(100vh - 320px)',
                                overflow: 'auto',
                                borderRadius: 0,

                                border: `1px solid ${theme.palette.divider}`,
                                '&::-webkit-scrollbar': {
                                    height: 8,
                                    width: 8
                                },
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: theme.palette.grey[400],
                                    borderRadius: 4
                                }
                            }}
                        >
                            <Table stickyHeader size="small">
                                <TableHead>
                                    {groupBy === 'broker' ? (
                                        <>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Broker</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('Broker', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Inv. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('invAmt', e)}>
                                                        {/* <FilterList fontSize="small" /> */}
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>TDS Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('TdsAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Pay. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('PayAmt', e)}>

                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Ret. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('retAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Balance Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('BalanceAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </>
                                    ) : groupBy === 'party' ? (
                                        <>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Party</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('Party', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Inv. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('invAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>TDS Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('TdsAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Pay. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('PayAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Ret. Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('retAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Balance Amount</span>
                                                    <IconButton size="small" onClick={(e) => handleFilterClick('BalanceAmt', e)}>
                                                        <FontAwesomeIcon icon={faFilter} className={headfilterStyle} />
                                                    </IconButton>
                                                </Box>
                                            </TableCell>
                                        </>
                                    ) : (
                                        <>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Party</span>

                                                    <FontAwesomeIcon className={headfilterStyle} onClick={(e) => handleFilterClick('Party', e)} icon={faFilter} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Broker</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('Broker', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Inv. Date</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('invDate', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Bill Type</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('voutype', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Inv. No</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('invno', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Due Days</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('DueDays', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Due Date</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('DueDate', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Inv. Amount</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('invAmt', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>TDS Amount</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('TdsAmt', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Pay. Amount</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('PayAmt', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Ret. Amount</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('retAmt', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Balance Amount</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('BalanceAmt', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                    <span>Diff.Days Inv.Date</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('dif_day_inv_date', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>
                                            <TableCell sx={{ ...headCssStyle }}>
                                                <Box display="flex" alignItems="top" justifyContent="space-between">
                                                    <span>Diff.Days Due.Date</span>
                                                    <FontAwesomeIcon onClick={(e) => handleFilterClick('dif_day_due_date', e)} icon={faFilter} className={headfilterStyle} />
                                                </Box>
                                            </TableCell>

                                        </>
                                    )}
                                </TableHead>
                                <TableBody>
                                    {filteredAndGroupedData.map((row, index) => (
                                        <TableRow key={index} hover>
                                            {groupBy === 'broker' ? (
                                                <>
                                                    <TableCell sx={{ fontSize: "0.7rem" }}>{row.Broker || 'N/A'}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.invAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.TdsAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.PayAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.retAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.BalanceAmt)}</TableCell>
                                                </>
                                            ) : groupBy === 'party' ? (
                                                <>
                                                    <TableCell sx={{ fontSize: "0.7rem" }} >{row.Party || 'N/A'}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.invAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.TdsAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.PayAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.retAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.BalanceAmt)}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell sx={{ fontSize: "0.7rem" }}>{row.Party || 'N/A'}</TableCell>
                                                    <TableCell sx={{ fontSize: "0.7rem" }}>{row.Broker || 'N/A'}</TableCell>
                                                    <TableCell>  {row.invDate ? format(parse(row.invDate, 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy') : ''}</TableCell>
                                                    <TableCell>{row.voutype}</TableCell>
                                                    <TableCell>{row.invno}</TableCell>
                                                    <TableCell align="right">{row.DueDays}</TableCell>
                                                    <TableCell>  {row.DueDate ? format(parse(row.DueDate, 'dd-MM-yyyy', new Date()), 'dd/MM/yyyy') : ''}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.invAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.TdsAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.PayAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.retAmt)}</TableCell>
                                                    <TableCell align="right">{formatCurrency(row.BalanceAmt)}</TableCell>
                                                    <TableCell align="right">{row.dif_day_inv_date}</TableCell>
                                                    <TableCell align="right">{row.dif_day_due_date}</TableCell>
                                                </>
                                            )}

                                        </TableRow>

                                    ))}
                                    {/*Grand Total*/}
                                    {showReport && filteredAndGroupedData.length > 0 && (
                                        <TableRow sx={{ backgroundColor: theme.palette.grey[200] }}>
                                            {groupBy === 'broker' || groupBy === 'party' ? (
                                                <>
                                                    <TableCell align="left" sx={{ fontWeight: 'bold' }}>Grand Total:</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.invAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.TdsAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.PayAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.retAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.BalanceAmt)}</TableCell>
                                                </>
                                            ) : (
                                                <>
                                                    <TableCell align="left" colSpan={7} sx={{ fontWeight: 'bold' }}>Grand Total:</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.invAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.TdsAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.PayAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.retAmt)}</TableCell>
                                                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>{formatCurrency(totals.BalanceAmt)}</TableCell>
                                                    <TableCell colSpan={3}></TableCell>
                                                </>
                                            )}
                                        </TableRow>
                                    )}

                                </TableBody>
                            </Table>
                        </TableContainer>
                    )
                        : (
                            <Box sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                p: 4,
                                minHeight: 200,
                                backgroundColor: 'background.default',
                                borderRadius: 2
                            }}>
                                <FontAwesomeIcon icon={faInfo} />
                                {/* <DescriptionOutlined sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} /> */}
                                <Typography variant="body1" color="text.secondary">
                                    {isFetchingDetails ? 'Loading report...' : 'Submit the form to generate the report'}
                                </Typography>
                                {isFetchingDetails && <CircularProgress size={24} sx={{ mt: 2 }} />}
                            </Box>
                        )}

                </Paper>

                {/* Column Filter Popover */}
                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleFilterClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left',
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left',
                    }}
                    PaperProps={{
                        sx: {
                            borderRadius: 2,
                            boxShadow: '0px 5px 15px rgba(0, 0, 0, 0.1)',
                            overflow: 'visible',
                            '&:before': {
                                content: '""',
                                display: 'block',
                                position: 'absolute',
                                top: 0,
                                left: 14,
                                width: 10,
                                height: 10,
                                bgcolor: 'background.paper',
                                transform: 'translateY(-50%) rotate(45deg)',
                                zIndex: 0
                            }
                        }
                    }}
                >
                    <Box sx={{
                        p: 2,
                        width: 250,
                        position: 'relative',
                        zIndex: 1
                    }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Filter by {currentFilterColumn}
                        </Typography>
                        <TextField
                            fullWidth
                            value={columnFilters[currentFilterColumn] || ''}
                            onChange={handleColumnFilterChange}
                            variant="outlined"
                            size="small"
                            autoFocus
                            InputProps={{
                                endAdornment: columnFilters[currentFilterColumn] && (
                                    <InputAdornment position="end">
                                        <IconButton
                                            size="small"
                                            onClick={() => clearColumnFilter(currentFilterColumn)}
                                            edge="end"
                                        >
                                            <FontAwesomeIcon icon={faClose} className='text-sm' />
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                            sx={{ borderRadius: 1 }}
                        />
                    </Box>
                </Popover>
            </Box >
        </MainContainer >
    );
};

export default OutstandingReport;